/**
 * Ties this browser to the signed-in account, once per established session.
 *
 * The server issues a short single-use challenge, the browser runs one isolated
 * identification tagged with it, and the server validates everything again. We
 * never send an account identifier to Fingerprint and never block the app: a
 * failure simply means no association for this session.
 */
import { supabase } from '@/integrations/supabase/client';
import { identifyForLoginProof } from '@/lib/fingerprint';

const STORAGE_PREFIX = 'trimr.fp_proof.';
const done = new Set<string>();

/**
 * Stable identifier of the auth session, read from the `session_id` claim.
 * `expires_at` cannot be used: it changes on every token refresh, which would
 * re-run the proof. Decoding the token here is only a client-side guard — the
 * server always resolves the user with getUser().
 */
function sessionIdFromToken(accessToken: string): string | null {
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join(''),
      ),
    ) as { session_id?: unknown };
    return typeof payload === 'string' && typeof json.session_id === 'string' ? json.session_id : null;
  } catch {
    return null;
  }
}

function sessionKey(userId: string, sessionId: string | null) {
  return `${STORAGE_PREFIX}${userId}.${sessionId ?? 'unknown'}`;
}

/** True once per auth session (survives JWT refreshes and remounts). */
function claimOnce(key: string): boolean {
  if (done.has(key)) return false;
  done.add(key);
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
  } catch {
    /* in-memory guard is enough */
  }
  return true;
}

export async function linkBrowserToAccount(accessToken: string, userId: string) {
  if (!accessToken || !userId) return;
  if (!claimOnce(sessionKey(userId, sessionIdFromToken(accessToken)))) return;

  try {
    const { data: challenge, error } = await supabase.functions.invoke('fp-link-account', {
      headers: { Authorization: `Bearer ${accessToken}` },
      body: { action: 'challenge' },
    });
    const nonce = typeof challenge?.nonce === 'string' ? challenge.nonce : null;
    if (error || !nonce) return;

    const proof = await identifyForLoginProof(nonce);
    if (proof.status !== 'success' || !proof.requestId) return;

    await supabase.functions.invoke('fp-link-account', {
      headers: { Authorization: `Bearer ${accessToken}` },
      body: { action: 'confirm', nonce, fp_request_id: proof.requestId },
    });
  } catch {
    /* never block the session on a proof failure */
  }
}
